import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Modal, Platform, TouchableWithoutFeedback, ScrollView } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Picker } from '@react-native-picker/picker';
import {
  CheckSquare, Search, X, ChevronDown, ChevronRight, CheckCircle2,
  Clock, Calendar, Home as HomeIcon, User, UserCheck, AlertTriangle,
  ExternalLink, ClipboardList, Edit3, Trash2,
} from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { AdminTask } from './types';
import { shadows } from '../../lib/styles';
import { formatDateOnly, parseLocalDate } from '../../lib/dateUtils';
import DateInput from '../../components/ui/DateInput';

type StatusFilter = AdminTask['status'];
type PriorityFilter = AdminTask['priority'];

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return { color: '#dc2626', backgroundColor: '#fee2e2' };
    case 'high': return { color: '#ea580c', backgroundColor: '#ffedd5' };
    case 'normal': return { color: '#2563eb', backgroundColor: '#dbeafe' };
    case 'low': return { color: '#6b7280', backgroundColor: '#f3f4f6' };
    default: return { color: '#6b7280', backgroundColor: '#f3f4f6' };
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return { color: '#ea580c', backgroundColor: '#ffedd5' };
    case 'in_progress': return { color: '#2563eb', backgroundColor: '#dbeafe' };
    case 'completed': return { color: '#16a34a', backgroundColor: '#dcfce7' };
    default: return { color: '#6b7280', backgroundColor: '#f3f4f6' };
  }
};

const TasksTab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [systemManagers, setSystemManagers] = useState<Array<{ id: string; full_name: string }>>([]);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // URL-driven filters
  const statusFromUrl = searchParams.get('taskStatus');
  const priorityFromUrl = searchParams.get('taskPriority');
  const assigneeFromUrl = searchParams.get('taskAssignee');
  const searchFromUrl = searchParams.get('taskSearch') || '';

  const parseStatusFromUrl = (param: string | null): StatusFilter[] => {
    if (!param) return [];
    return param.split(',').filter(s => ['pending', 'in_progress', 'completed'].includes(s)) as StatusFilter[];
  };

  const parsePriorityFromUrl = (param: string | null): PriorityFilter[] => {
    if (!param) return [];
    return param.split(',').filter(p => ['low', 'normal', 'high', 'urgent'].includes(p)) as PriorityFilter[];
  };

  const parseAssigneeFromUrl = (param: string | null): string[] => {
    if (!param) return [];
    return param.split(',').filter(Boolean);
  };

  const [statusFilter, setStatusFilter] = useState<StatusFilter[]>(parseStatusFromUrl(statusFromUrl));
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter[]>(parsePriorityFromUrl(priorityFromUrl));
  const [assigneeFilter, setAssigneeFilter] = useState<string[]>(parseAssigneeFromUrl(assigneeFromUrl));
  const [searchQuery, setSearchQuery] = useState(searchFromUrl);

  const [showStatusFilterDropdown, setShowStatusFilterDropdown] = useState(false);
  const [showPriorityFilterDropdown, setShowPriorityFilterDropdown] = useState(false);
  const [showAssigneeFilterDropdown, setShowAssigneeFilterDropdown] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AdminTask | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<AdminTask | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<AdminTask | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    priority: 'normal' as AdminTask['priority'],
    status: 'pending' as AdminTask['status'],
    due_date: '',
    assigned_to: '',
  });

  // Sync filters to URL
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    const currentTab = searchParams.get('tab');
    if (currentTab) newParams.set('tab', currentTab);

    if (statusFilter.length > 0) newParams.set('taskStatus', statusFilter.join(','));
    else newParams.delete('taskStatus');

    if (priorityFilter.length > 0) newParams.set('taskPriority', priorityFilter.join(','));
    else newParams.delete('taskPriority');

    if (assigneeFilter.length > 0) newParams.set('taskAssignee', assigneeFilter.join(','));
    else newParams.delete('taskAssignee');

    if (searchQuery.trim()) newParams.set('taskSearch', searchQuery.trim());
    else newParams.delete('taskSearch');

    setSearchParams(newParams, { replace: true });
  }, [statusFilter, priorityFilter, assigneeFilter, searchQuery]);

  // Sync URL back to state on external navigation
  useEffect(() => {
    setStatusFilter(prev => {
      const next = parseStatusFromUrl(searchParams.get('taskStatus'));
      return JSON.stringify(prev) !== JSON.stringify(next) ? next : prev;
    });
    setPriorityFilter(prev => {
      const next = parsePriorityFromUrl(searchParams.get('taskPriority'));
      return JSON.stringify(prev) !== JSON.stringify(next) ? next : prev;
    });
    setAssigneeFilter(prev => {
      const next = parseAssigneeFromUrl(searchParams.get('taskAssignee'));
      return JSON.stringify(prev) !== JSON.stringify(next) ? next : prev;
    });
    setSearchQuery(prev => {
      const next = searchParams.get('taskSearch') || '';
      return next !== prev ? next : prev;
    });
  }, [searchParams]);

  useEffect(() => {
    fetchTasks();
    fetchSystemManagers();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('admin_tasks')
        .select(`
          *,
          home:homes ( address, city, state ),
          homeowner:profiles!homeowner_id ( full_name ),
          service_request:service_requests!service_request_id ( title ),
          creator:profiles!created_by ( full_name ),
          assignee:profiles!assigned_to ( full_name )
        `)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTasks((data || []) as any);
    } catch (err: any) {
      console.error('Error fetching admin tasks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemManagers = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name');

      if (fetchError) throw fetchError;
      setSystemManagers(data || []);
    } catch (err) {
      console.error('Error fetching system managers:', err);
    }
  };

  const handleStatusUpdate = async (taskId: string, newStatus: AdminTask['status']) => {
    try {
      setUpdatingId(taskId);
      const updateData: Record<string, any> = { status: newStatus };
      if (newStatus === 'completed') updateData.completed_at = new Date().toISOString();
      else updateData.completed_at = null;

      const { error: updateError } = await supabase
        .from('admin_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (updateError) throw updateError;

      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, status: newStatus, completed_at: updateData.completed_at } : t
      ));
      setShowStatusModal(false);
      setSelectedTask(null);
    } catch (err: any) {
      console.error('Error updating task status:', err);
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOpenEditModal = (task: AdminTask) => {
    setEditingTask(task);
    setEditFormData({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority,
      status: task.status,
      due_date: task.due_date ? formatDateOnly(task.due_date) : '',
      assigned_to: task.assigned_to || '',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingTask(null);
  };

  const handleSaveEdit = async () => {
    if (!editingTask || !editFormData.title.trim()) return;

    setSavingEdit(true);
    try {
      const dueDateIso = editFormData.due_date
        ? (parseLocalDate(editFormData.due_date)?.toISOString() || null)
        : null;

      const updates = {
        title: editFormData.title.trim(),
        description: editFormData.description.trim() || null,
        priority: editFormData.priority,
        status: editFormData.status,
        due_date: dueDateIso,
        assigned_to: editFormData.assigned_to || null,
        completed_at: editFormData.status === 'completed'
          ? (editingTask.completed_at || new Date().toISOString())
          : null,
      };

      const { error: updateError } = await supabase
        .from('admin_tasks')
        .update(updates)
        .eq('id', editingTask.id);

      if (updateError) throw updateError;

      const assignee = editFormData.assigned_to
        ? systemManagers.find(m => m.id === editFormData.assigned_to)
        : null;

      setTasks(prev => prev.map(t =>
        t.id === editingTask.id
          ? {
              ...t,
              ...updates,
              assignee: assignee ? { full_name: assignee.full_name } : null,
            }
          : t
      ));
      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenDeleteModal = (task: AdminTask) => {
    setTaskToDelete(task);
    setShowDeleteModal(true);
    handleCloseEditModal();
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    setDeletingTask(true);
    try {
      const { error: deleteError } = await supabase
        .from('admin_tasks')
        .delete()
        .eq('id', taskToDelete.id);

      if (deleteError) throw deleteError;

      setTasks(prev => prev.filter(t => t.id !== taskToDelete.id));
      setExpandedCards(prev => {
        const next = new Set(prev);
        next.delete(taskToDelete.id);
        return next;
      });
      setShowDeleteModal(false);
      setTaskToDelete(null);
    } catch (err: any) {
      console.error('Error deleting task:', err);
      setError(err.message);
    } finally {
      setDeletingTask(false);
    }
  };

  const toggleCardExpanded = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleStatusFilter = (s: StatusFilter) => {
    setStatusFilter(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const togglePriorityFilter = (p: PriorityFilter) => {
    setPriorityFilter(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const toggleAssigneeFilter = (id: string) => {
    setAssigneeFilter(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Client-side filtering
  const filteredTasks = tasks.filter(task => {
    if (statusFilter.length > 0 && !statusFilter.includes(task.status)) return false;
    if (priorityFilter.length > 0 && !priorityFilter.includes(task.priority)) return false;
    if (assigneeFilter.length > 0) {
      if (assigneeFilter.includes('unassigned')) {
        if (task.assigned_to && !assigneeFilter.includes(task.assigned_to)) return false;
      } else if (!assigneeFilter.includes(task.assigned_to)) {
        return false;
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = task.title.toLowerCase().includes(q);
      const matchDesc = task.description?.toLowerCase().includes(q);
      const matchHomeowner = (task as any).homeowner?.full_name?.toLowerCase().includes(q);
      const matchHome = (task as any).home?.address?.toLowerCase().includes(q);
      const matchServiceRequest = (task as any).service_request?.title?.toLowerCase().includes(q);
      if (!matchTitle && !matchDesc && !matchHomeowner && !matchHome && !matchServiceRequest) return false;
    }
    return true;
  });

  const statusCounts = {
    pending: filteredTasks.filter(t => t.status === 'pending').length,
    in_progress: filteredTasks.filter(t => t.status === 'in_progress').length,
    completed: filteredTasks.filter(t => t.status === 'completed').length,
  };

  const getStatusFilterLabel = () => {
    if (statusFilter.length === 0) return 'All Statuses';
    if (statusFilter.length === 1) {
      return statusFilter[0].split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    return `${statusFilter.length} Statuses`;
  };

  const getPriorityFilterLabel = () => {
    if (priorityFilter.length === 0) return 'All Priorities';
    if (priorityFilter.length === 1) {
      return priorityFilter[0].charAt(0).toUpperCase() + priorityFilter[0].slice(1);
    }
    return `${priorityFilter.length} Priorities`;
  };

  const getAssigneeFilterLabel = () => {
    if (assigneeFilter.length === 0) return 'All Assignees';
    if (assigneeFilter.length === 1) {
      if (assigneeFilter[0] === 'unassigned') return 'Unassigned';
      const mgr = systemManagers.find(m => m.id === assigneeFilter[0]);
      return mgr?.full_name || assigneeFilter[0];
    }
    return `${assigneeFilter.length} Assignees`;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Error loading tasks</Text>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  return (
    <>
      <WebScrollView style={styles.tabScroll} contentContainerStyle={styles.tabScrollContent}>
        <View style={styles.header}>
          {/* Title and Search */}
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <CheckSquare size={24} color="#9ca3af" />
              <Text style={styles.title}>Tasks</Text>
            </View>
            <View style={styles.searchContainer}>
              <Search size={16} color="#9ca3af" />
              <TextInput
                style={styles.searchInput}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search tasks..."
                placeholderTextColor="#9ca3af"
              />
              {searchQuery.length > 0 && (
                <Pressable onPress={() => setSearchQuery('')}>
                  <X size={16} color="#9ca3af" />
                </Pressable>
              )}
            </View>
          </View>

          {/* Filters */}
          <View style={styles.headerRow}>
            <View style={styles.headerActions}>
              {/* Status Filter */}
              <View style={styles.filterContainer}>
                <Pressable
                  style={styles.filterButton}
                  onPress={() => setShowStatusFilterDropdown(!showStatusFilterDropdown)}
                >
                  <Text style={styles.filterButtonText}>{getStatusFilterLabel()}</Text>
                  <Text style={styles.filterButtonIcon}>▼</Text>
                </Pressable>

                {showStatusFilterDropdown && (
                  <Modal visible transparent animationType="fade" onRequestClose={() => setShowStatusFilterDropdown(false)}>
                    <TouchableWithoutFeedback onPress={() => setShowStatusFilterDropdown(false)}>
                      <View style={styles.modalOverlay}>
                        <View style={styles.filterDropdown}>
                          <Pressable
                            style={styles.filterItem}
                            onPress={() => { setStatusFilter([]); setShowStatusFilterDropdown(false); }}
                          >
                            <Text style={styles.filterItemText}>All Statuses</Text>
                            {statusFilter.length === 0 && <CheckCircle2 size={16} color="#000000" />}
                          </Pressable>
                          <View style={styles.filterDivider} />
                          {(['pending', 'in_progress', 'completed'] as const).map(s => (
                            <Pressable key={s} style={styles.filterItem} onPress={() => toggleStatusFilter(s)}>
                              <View style={styles.filterCheckbox}>
                                {statusFilter.includes(s) && <CheckCircle2 size={12} color="#ffffff" />}
                              </View>
                              <Text style={styles.filterItemText}>
                                {s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      </View>
                    </TouchableWithoutFeedback>
                  </Modal>
                )}
              </View>

              {/* Priority Filter */}
              <View style={styles.filterContainer}>
                <Pressable
                  style={styles.filterButton}
                  onPress={() => setShowPriorityFilterDropdown(!showPriorityFilterDropdown)}
                >
                  <Text style={styles.filterButtonText}>{getPriorityFilterLabel()}</Text>
                  <Text style={styles.filterButtonIcon}>▼</Text>
                </Pressable>

                {showPriorityFilterDropdown && (
                  <Modal visible transparent animationType="fade" onRequestClose={() => setShowPriorityFilterDropdown(false)}>
                    <TouchableWithoutFeedback onPress={() => setShowPriorityFilterDropdown(false)}>
                      <View style={styles.modalOverlay}>
                        <View style={styles.filterDropdown}>
                          <Pressable
                            style={styles.filterItem}
                            onPress={() => { setPriorityFilter([]); setShowPriorityFilterDropdown(false); }}
                          >
                            <Text style={styles.filterItemText}>All Priorities</Text>
                            {priorityFilter.length === 0 && <CheckCircle2 size={16} color="#000000" />}
                          </Pressable>
                          <View style={styles.filterDivider} />
                          {(['urgent', 'high', 'normal', 'low'] as const).map(p => (
                            <Pressable key={p} style={styles.filterItem} onPress={() => togglePriorityFilter(p)}>
                              <View style={styles.filterCheckbox}>
                                {priorityFilter.includes(p) && <CheckCircle2 size={12} color="#ffffff" />}
                              </View>
                              <Text style={styles.filterItemText}>
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      </View>
                    </TouchableWithoutFeedback>
                  </Modal>
                )}
              </View>

              {/* Assignee Filter */}
              <View style={styles.filterContainer}>
                <Pressable
                  style={styles.filterButton}
                  onPress={() => setShowAssigneeFilterDropdown(!showAssigneeFilterDropdown)}
                >
                  <Text style={styles.filterButtonText}>{getAssigneeFilterLabel()}</Text>
                  <Text style={styles.filterButtonIcon}>▼</Text>
                </Pressable>

                {showAssigneeFilterDropdown && (
                  <Modal visible transparent animationType="fade" onRequestClose={() => setShowAssigneeFilterDropdown(false)}>
                    <TouchableWithoutFeedback onPress={() => setShowAssigneeFilterDropdown(false)}>
                      <View style={styles.modalOverlay}>
                        <View style={styles.filterDropdown}>
                          <Pressable
                            style={styles.filterItem}
                            onPress={() => { setAssigneeFilter([]); setShowAssigneeFilterDropdown(false); }}
                          >
                            <Text style={styles.filterItemText}>All Assignees</Text>
                            {assigneeFilter.length === 0 && <CheckCircle2 size={16} color="#000000" />}
                          </Pressable>
                          <View style={styles.filterDivider} />
                          {systemManagers.map(mgr => (
                            <Pressable key={mgr.id} style={styles.filterItem} onPress={() => toggleAssigneeFilter(mgr.id)}>
                              <View style={styles.filterCheckbox}>
                                {assigneeFilter.includes(mgr.id) && <CheckCircle2 size={12} color="#ffffff" />}
                              </View>
                              <Text style={styles.filterItemText}>{mgr.full_name}</Text>
                            </Pressable>
                          ))}
                        </View>
                      </View>
                    </TouchableWithoutFeedback>
                  </Modal>
                )}
              </View>

              <Pressable style={styles.refreshButton} onPress={fetchTasks} disabled={loading}>
                {loading ? (
                  <ActivityIndicator size="small" color="#4b5563" />
                ) : (
                  <Text style={styles.refreshButtonText}>Refresh</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>

        {/* Summary */}
        <View style={styles.summaryWidget}>
          <Text style={styles.summaryWidgetTitle}>Summary</Text>
          <View style={styles.statusSummaryGrid}>
            <View style={styles.statusSummaryItem}>
              <View style={[styles.statusSummaryBadge, { backgroundColor: '#ffedd5' }]}>
                <Text style={[styles.statusSummaryCount, { color: '#ea580c' }]}>{statusCounts.pending}</Text>
              </View>
              <Text style={styles.statusSummaryLabel}>Pending</Text>
            </View>
            <View style={styles.statusSummaryItem}>
              <View style={[styles.statusSummaryBadge, { backgroundColor: '#dbeafe' }]}>
                <Text style={[styles.statusSummaryCount, { color: '#2563eb' }]}>{statusCounts.in_progress}</Text>
              </View>
              <Text style={styles.statusSummaryLabel}>In Progress</Text>
            </View>
            <View style={styles.statusSummaryItem}>
              <View style={[styles.statusSummaryBadge, { backgroundColor: '#dcfce7' }]}>
                <Text style={[styles.statusSummaryCount, { color: '#16a34a' }]}>{statusCounts.completed}</Text>
              </View>
              <Text style={styles.statusSummaryLabel}>Completed</Text>
            </View>
          </View>
        </View>

        {/* Task List */}
        {filteredTasks.length === 0 ? (
          <View style={styles.emptyContainer}>
            <AlertTriangle size={48} color="#9ca3af" />
            <Text style={styles.emptyTitle}>No Tasks Found</Text>
            <Text style={styles.emptyText}>
              {statusFilter.length === 0 && priorityFilter.length === 0 && assigneeFilter.length === 0 && !searchQuery.trim()
                ? 'There are no tasks in the system.'
                : `No tasks match the selected filters${searchQuery.trim() ? ' or search query' : ''}.`}
            </Text>
            {searchQuery.trim() && (
              <Pressable style={styles.clearSearchButton} onPress={() => setSearchQuery('')}>
                <Text style={styles.clearSearchButtonText}>Clear Search</Text>
              </Pressable>
            )}
          </View>
        ) : (
          <View style={styles.tasksList}>
            {filteredTasks.map(task => {
              const priorityColors = getPriorityColor(task.priority);
              const statusColors = getStatusColor(task.status);
              const isExpanded = expandedCards.has(task.id);
              const assignee = (task as any).assignee;
              const creator = (task as any).creator;

              return (
                <View key={task.id} style={styles.compactCard}>
                  <Pressable style={styles.compactCardHeader} onPress={() => toggleCardExpanded(task.id)}>
                    <View style={styles.compactCardLeft}>
                      {isExpanded ? (
                        <ChevronDown size={14} color="#9ca3af" />
                      ) : (
                        <ChevronRight size={14} color="#9ca3af" />
                      )}
                      <Pressable
                        style={[styles.compactStatusBadge, { backgroundColor: statusColors.backgroundColor }]}
                        onPress={(e) => {
                          e.stopPropagation();
                          setSelectedTask(task);
                          setShowStatusModal(true);
                        }}
                      >
                        <Text style={[styles.compactStatusText, { color: statusColors.color }]}>
                          {task.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </Text>
                      </Pressable>
                      <View style={[styles.compactPriorityDot, { backgroundColor: priorityColors.color }]} />
                      <Text style={styles.compactTitle} numberOfLines={1}>{task.title}</Text>
                      {(task as any).homeowner?.full_name && (
                        <Text style={styles.compactOwnerName} numberOfLines={1}>
                          {(task as any).homeowner.full_name}
                        </Text>
                      )}
                    </View>
                    <View style={styles.compactCardRight}>
                      {assignee && (
                        <View style={styles.compactAssignedBadge}>
                          <UserCheck size={11} color="#16a34a" />
                          <Text style={styles.compactAssignedText} numberOfLines={1}>
                            {assignee.full_name}
                          </Text>
                        </View>
                      )}
                      {task.due_date && (
                        <View style={styles.compactDueDateBadge}>
                          <Calendar size={11} color={new Date(task.due_date) < new Date() && task.status !== 'completed' ? '#dc2626' : '#6b7280'} />
                          <Text style={[
                            styles.compactDueDateText,
                            new Date(task.due_date) < new Date() && task.status !== 'completed' && styles.compactDueDateOverdue,
                          ]}>
                            {new Date(task.due_date).toLocaleDateString()}
                          </Text>
                        </View>
                      )}
                    </View>
                  </Pressable>

                  {isExpanded && (
                    <View style={styles.compactCardBody}>
                      {task.description && (
                        <Text style={styles.taskDescription}>{task.description}</Text>
                      )}
                      <View style={styles.taskMeta}>
                        {task.home && (
                          <View style={styles.metaItem}>
                            <HomeIcon size={14} color="#9ca3af" />
                            <Text style={styles.metaText}>
                              {task.home.address}, {task.home.city}, {task.home.state}
                            </Text>
                          </View>
                        )}
                        {(task as any).service_request?.title && (
                          <View style={styles.metaItem}>
                            <ClipboardList size={14} color="#9ca3af" />
                            <Text style={styles.metaText}>{(task as any).service_request.title}</Text>
                          </View>
                        )}
                        <View style={styles.metaItem}>
                          <Clock size={14} color="#9ca3af" />
                          <Text style={styles.metaText}>
                            Created {new Date(task.created_at).toLocaleDateString()}
                          </Text>
                        </View>
                        {creator && (
                          <View style={styles.metaItem}>
                            <User size={14} color="#9ca3af" />
                            <Text style={styles.metaText}>
                              Created by {creator.full_name}
                            </Text>
                          </View>
                        )}
                        {task.completed_at && (
                          <View style={styles.metaItem}>
                            <CheckCircle2 size={14} color="#16a34a" />
                            <Text style={styles.metaText}>
                              Completed {new Date(task.completed_at).toLocaleDateString()}
                            </Text>
                          </View>
                        )}
                      </View>
                      <View style={styles.compactCardActions}>
                        <View style={[styles.badge, { backgroundColor: priorityColors.backgroundColor }]}>
                          <Text style={[styles.badgeText, { color: priorityColors.color }]}>
                            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                          </Text>
                        </View>
                        <Pressable
                          style={styles.linkButton}
                          onPress={() => handleOpenEditModal(task)}
                        >
                          <Edit3 size={12} color="#2563eb" />
                          <Text style={styles.linkButtonText}>Edit</Text>
                        </Pressable>
                        {task.service_request_id && (
                          <Pressable
                            style={styles.linkButton}
                            onPress={() => navigate(`/admin/service-request/${task.service_request_id}`)}
                          >
                            <ExternalLink size={12} color="#2563eb" />
                            <Text style={styles.linkButtonText}>View Request</Text>
                          </Pressable>
                        )}
                        <Pressable
                          style={styles.deleteLinkButton}
                          onPress={() => handleOpenDeleteModal(task)}
                        >
                          <Trash2 size={12} color="#dc2626" />
                          <Text style={styles.deleteLinkButtonText}>Delete</Text>
                        </Pressable>
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </WebScrollView>

      {/* Status Change Modal */}
      {showStatusModal && selectedTask && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setShowStatusModal(false)}>
          <TouchableWithoutFeedback onPress={() => setShowStatusModal(false)}>
            <View style={styles.modalOverlay}>
              <View style={styles.statusChangeModal}>
                <Text style={styles.statusChangeTitle}>Update Status</Text>
                <Text style={styles.statusChangeSubtitle} numberOfLines={1}>{selectedTask.title}</Text>
                {(['pending', 'in_progress', 'completed'] as const).map(s => {
                  const colors = getStatusColor(s);
                  return (
                    <Pressable
                      key={s}
                      style={[styles.statusChangeOption, selectedTask.status === s && styles.statusChangeOptionActive]}
                      onPress={() => handleStatusUpdate(selectedTask.id, s)}
                      disabled={updatingId === selectedTask.id}
                    >
                      <View style={[styles.statusChangeDot, { backgroundColor: colors.color }]} />
                      <Text style={styles.statusChangeOptionText}>
                        {s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Text>
                      {selectedTask.status === s && <CheckCircle2 size={16} color="#000000" />}
                    </Pressable>
                  );
                })}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Edit Task Modal */}
      {showEditModal && editingTask && (
        <Modal visible transparent animationType="fade" onRequestClose={handleCloseEditModal}>
          <TouchableWithoutFeedback onPress={handleCloseEditModal}>
            <View style={styles.modalOverlay}>
              <TouchableWithoutFeedback>
                <View style={styles.editModal}>
                  <View style={styles.editModalHeader}>
                    <Text style={styles.editModalTitle}>Edit Task</Text>
                    <Pressable onPress={handleCloseEditModal} style={styles.editModalCloseButton}>
                      <X size={20} color="#9ca3af" />
                    </Pressable>
                  </View>

                  <ScrollView style={styles.editModalScroll} showsVerticalScrollIndicator>
                    <View style={styles.editModalBody}>
                      <View style={styles.formField}>
                        <Text style={styles.formLabel}>Task Title *</Text>
                        <TextInput
                          style={styles.formInput}
                          value={editFormData.title}
                          onChangeText={(text) => setEditFormData(prev => ({ ...prev, title: text }))}
                          placeholder="Task title"
                        />
                      </View>

                      <View style={styles.formField}>
                        <Text style={styles.formLabel}>Description (Optional)</Text>
                        <TextInput
                          style={styles.formTextArea}
                          value={editFormData.description}
                          onChangeText={(text) => setEditFormData(prev => ({ ...prev, description: text }))}
                          placeholder="Task description"
                          multiline
                          numberOfLines={3}
                        />
                      </View>

                      <View style={styles.formField}>
                        <Text style={styles.formLabel}>Priority</Text>
                        <View style={styles.pickerContainer}>
                          <Picker
                            selectedValue={editFormData.priority}
                            onValueChange={(value) => setEditFormData(prev => ({ ...prev, priority: value as AdminTask['priority'] }))}
                            style={styles.picker}
                          >
                            <Picker.Item label="Low" value="low" />
                            <Picker.Item label="Normal" value="normal" />
                            <Picker.Item label="High" value="high" />
                            <Picker.Item label="Urgent" value="urgent" />
                          </Picker>
                        </View>
                      </View>

                      <View style={styles.formField}>
                        <Text style={styles.formLabel}>Status</Text>
                        <View style={styles.pickerContainer}>
                          <Picker
                            selectedValue={editFormData.status}
                            onValueChange={(value) => setEditFormData(prev => ({ ...prev, status: value as AdminTask['status'] }))}
                            style={styles.picker}
                          >
                            <Picker.Item label="Pending" value="pending" />
                            <Picker.Item label="In Progress" value="in_progress" />
                            <Picker.Item label="Completed" value="completed" />
                          </Picker>
                        </View>
                      </View>

                      <View style={styles.formField}>
                        <Text style={styles.formLabel}>Due Date (Optional)</Text>
                        <DateInput
                          value={editFormData.due_date}
                          onChange={(value) => setEditFormData(prev => ({ ...prev, due_date: value }))}
                          inputStyle={styles.formInput}
                        />
                      </View>

                      <View style={styles.formField}>
                        <Text style={styles.formLabel}>Assign To</Text>
                        <View style={styles.pickerContainer}>
                          <Picker
                            selectedValue={editFormData.assigned_to}
                            onValueChange={(value) => setEditFormData(prev => ({ ...prev, assigned_to: value }))}
                            style={styles.picker}
                          >
                            <Picker.Item label="Unassigned" value="" />
                            {systemManagers.map((manager) => (
                              <Picker.Item key={manager.id} label={manager.full_name} value={manager.id} />
                            ))}
                          </Picker>
                        </View>
                      </View>
                    </View>
                  </ScrollView>

                  <View style={styles.editModalActions}>
                    <Pressable
                      style={styles.deleteTaskButton}
                      onPress={() => handleOpenDeleteModal(editingTask)}
                      disabled={savingEdit}
                    >
                      <Trash2 size={14} color="#dc2626" />
                      <Text style={styles.deleteTaskButtonText}>Delete Task</Text>
                    </Pressable>
                    <View style={styles.editModalPrimaryActions}>
                      <Pressable
                        style={styles.modalCancelButton}
                        onPress={handleCloseEditModal}
                        disabled={savingEdit}
                      >
                        <Text style={styles.modalCancelButtonText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        style={[styles.modalSaveButton, (!editFormData.title.trim() || savingEdit) && styles.buttonDisabled]}
                        onPress={handleSaveEdit}
                        disabled={!editFormData.title.trim() || savingEdit}
                      >
                        {savingEdit ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.modalSaveButtonText}>Save Changes</Text>
                        )}
                      </Pressable>
                    </View>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && taskToDelete && (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={() => !deletingTask && setShowDeleteModal(false)}
        >
          <View style={styles.modalOverlay}>
            <Pressable
              style={styles.modalBackdrop}
              onPress={() => !deletingTask && setShowDeleteModal(false)}
            />
            <View style={styles.deleteModalContent}>
              <View style={styles.deleteModalHeader}>
                <View style={styles.deleteModalIconContainer}>
                  <Trash2 size={24} color="#dc2626" />
                </View>
                <Text style={styles.deleteModalTitle}>Delete Task</Text>
              </View>

              <View style={styles.deleteModalBody}>
                <Text style={styles.deleteModalText}>
                  Are you sure you want to delete "{taskToDelete.title}"? This action cannot be undone.
                </Text>
              </View>

              <View style={styles.deleteModalActions}>
                <Pressable
                  style={styles.deleteModalCancelButton}
                  onPress={() => {
                    if (!deletingTask) {
                      setShowDeleteModal(false);
                      setTaskToDelete(null);
                    }
                  }}
                  disabled={deletingTask}
                >
                  <Text style={styles.deleteModalCancelText}>Cancel</Text>
                </Pressable>
                <Pressable
                  style={[styles.deleteModalConfirmButton, deletingTask && styles.buttonDisabled]}
                  onPress={handleConfirmDelete}
                  disabled={deletingTask}
                >
                  {deletingTask ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.deleteModalConfirmText}>Delete</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  tabScroll: {
    flex: 1,
    minHeight: 0,
  },
  tabScrollContent: {
    padding: 16,
    gap: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
    minHeight: 200,
  },
  errorContainer: {
    padding: 24,
    gap: 8,
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#dc2626',
  },
  errorText: {
    fontSize: 14,
    color: '#6b7280',
  },
  header: {
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    minWidth: Platform.OS === 'web' ? 250 : 200,
    flex: Platform.OS === 'web' ? 0 : 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#000000',
    paddingVertical: 0,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  filterContainer: {
    position: 'relative' as const,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#000000',
  },
  filterButtonIcon: {
    fontSize: 12,
    color: '#6b7280',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterDropdown: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 4,
    minWidth: 200,
    ...shadows.lg,
  },
  filterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  filterItemText: {
    fontSize: 14,
    color: '#111827',
    flex: 1,
  },
  filterDivider: {
    height: 1,
    backgroundColor: '#f3f4f6',
    marginHorizontal: 8,
  },
  filterCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: '#111827',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Summary Widget
  summaryWidget: {
    marginTop: 16,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    gap: 12,
  },
  summaryWidgetTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statusSummaryGrid: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  statusSummaryItem: {
    alignItems: 'center',
    gap: 4,
    minWidth: 80,
  },
  statusSummaryBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    minWidth: 48,
    alignItems: 'center',
  },
  statusSummaryCount: {
    fontSize: 18,
    fontWeight: '700',
  },
  statusSummaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },

  // Empty state
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#000000',
    marginTop: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  clearSearchButton: {
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  clearSearchButtonText: {
    fontSize: 14,
    color: '#4b5563',
    fontWeight: '500',
  },

  // Compact card list
  tasksList: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  compactCard: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  compactCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  compactCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  compactStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  compactPriorityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
    minWidth: 0,
  },
  compactOwnerName: {
    fontSize: 12,
    color: '#6b7280',
    flexShrink: 0,
    maxWidth: 140,
  },
  compactCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  compactAssignedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    maxWidth: 120,
  },
  compactAssignedText: {
    fontSize: 11,
    color: '#16a34a',
    fontWeight: '500',
  },
  compactDueDateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  compactDueDateText: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '500',
  },
  compactDueDateOverdue: {
    color: '#dc2626',
    fontWeight: '600',
  },
  compactCardBody: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    backgroundColor: '#fafafa',
    gap: 10,
  },
  compactCardActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  taskDescription: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 20,
  },
  taskMeta: {
    gap: 6,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 13,
    color: '#6b7280',
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#bfdbfe',
    backgroundColor: '#eff6ff',
  },
  linkButtonText: {
    fontSize: 12,
    color: '#2563eb',
    fontWeight: '500',
  },
  deleteLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#fecaca',
    backgroundColor: '#fef2f2',
  },
  deleteLinkButtonText: {
    fontSize: 12,
    color: '#dc2626',
    fontWeight: '500',
  },

  // Status change modal
  statusChangeModal: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    minWidth: 280,
    gap: 12,
    ...shadows.lg,
  },
  statusChangeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  statusChangeSubtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 4,
  },
  statusChangeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 6,
  },
  statusChangeOptionActive: {
    backgroundColor: '#f3f4f6',
  },
  statusChangeDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  statusChangeOptionText: {
    fontSize: 14,
    color: '#111827',
    flex: 1,
  },

  // Edit modal
  editModal: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90%',
    ...shadows.lg,
  },
  editModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  editModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  editModalCloseButton: {
    padding: 4,
  },
  editModalScroll: {
    maxHeight: 420,
  },
  editModalBody: {
    padding: 20,
    gap: 16,
  },
  formField: {
    gap: 6,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  formTextArea: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 80,
    textAlignVertical: 'top',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  picker: {
    height: 40,
  },
  editModalActions: {
    padding: 20,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    gap: 12,
  },
  deleteTaskButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    paddingVertical: 6,
  },
  deleteTaskButtonText: {
    fontSize: 14,
    color: '#dc2626',
    fontWeight: '500',
  },
  editModalPrimaryActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  modalCancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  modalSaveButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#111827',
    minWidth: 120,
    alignItems: 'center',
  },
  modalSaveButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },

  // Delete modal
  deleteModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    ...shadows.lg,
  },
  deleteModalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  deleteModalIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fee2e2',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  deleteModalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  deleteModalBody: {
    marginBottom: 24,
  },
  deleteModalText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    lineHeight: 20,
  },
  deleteModalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  deleteModalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  deleteModalCancelText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  deleteModalConfirmButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
    backgroundColor: '#dc2626',
  },
  deleteModalConfirmText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
});

export default TasksTab;
